import { z } from "zod";
import {
  localizedTextSchema,
  svRequiredLocalizedTextSchema,
} from "./intake-template";

// TES-214 — "Frågor och svar" (FAQ). A searchable module of common, non-acute
// Q&A surfaced in the Messages area so patients can self-serve before
// contacting the care team. Admin/tenant-authored, localized {sv,en}. No PII.

// Admin upsert body — localized question/answer + optional category + ordering.
// TES-349 — an optional natural `key` (unique per tenant) makes an FAQ
// idempotently re-importable: a content sync in `replace` mode upserts by key
// instead of duplicating. Keyless entries (the legacy default) stay create-only.
// TES-361 — question/answer require non-empty `sv` (en falls back to sv at
// render time), and `published` defaults to FALSE: an imported/created FAQ is
// born draft and only becomes patient-visible when explicitly published.
export const faqUpsertSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9_-]+$/)
    .optional(),
  question: svRequiredLocalizedTextSchema,
  answer: svRequiredLocalizedTextSchema,
  category: z.string().optional(),
  order: z.number().int().min(0).default(0),
  published: z.boolean().default(false),
});
export type FaqUpsert = z.infer<typeof faqUpsertSchema>;

// Admin DTO — localized, both locales, for the editor list.
export const faqEntryDtoSchema = z.object({
  id: z.string(),
  key: z.string().nullable().optional(),
  question: localizedTextSchema,
  answer: localizedTextSchema,
  category: z.string().nullable().optional(),
  order: z.number().int(),
  published: z.boolean(),
  updatedAt: z.string(),
});
export type FaqEntryDto = z.infer<typeof faqEntryDtoSchema>;

// TES-1296 — "Om din behandling och uppföljning", the FAQ under Din vård
// (09C §5D, A-02 "OFFICIELLA LÄKEMEDELSGUIDER" §5). Its rows are stored FAQ
// entries with this category; they are served only by
// GET /patient-api/v1/faq/om-din-behandling, filtered by her situation, and
// never in the general "Frågor och svar" list.
export const ABOUT_YOUR_TREATMENT_FAQ_CATEGORY = "om_din_behandling";

// TES-1296 — where an answer sends her, by kind rather than by URL, so the
// destination has one source:
// - `official_guide`: the official instruction for HER product and pen, the
//   same link Din behandling shows ("Båda länkar till samma kanoniska
//   injektionsguide"). The app takes it from her current treatment; with no
//   guide there, the answer is shown without a link.
// - `support_menu`: "Så fungerar ditt stöd", the only lasting source for
//   coach, doctor, contact routes and reply times (09C §5D). The answer
//   points there instead of repeating it.
export const FAQ_LINK_KINDS = ["official_guide", "support_menu"] as const;
export const faqLinkKindSchema = z.enum(FAQ_LINK_KINDS);
export type FaqLinkKind = z.infer<typeof faqLinkKindSchema>;

// Patient-facing item — resolved to the patient's locale; the mobile client
// filters/searches over the returned list (small dataset).
export const faqItemSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  category: z.string().nullable().optional(),
  // TES-1296 — optional and additive: only the Om din behandling list sets it.
  link: faqLinkKindSchema.optional(),
});
export type FaqItem = z.infer<typeof faqItemSchema>;
